// components/SnFindFormContols.tsx

import React from "react";
import SnTextBox from "./SnTextBox";
import SnNumberBox from "./SnNumberBox";
import SnDateBox from "./SnDateBox";
import SnPeriodBox from "./SnPeriodBox";
import SnCodeBox from "./SnCodeBox";
import SnCodeNTextBox from "./SnCodeNTextBox";
import SnComboBox from "./SnComboBox";
import SnStepUp from "./SnStepUp";
import SnMemoBox from "./SnMemoBox";
import SnButton from "./SnButton";
import SnGroupLabel from "./SnGroupLabel";
import SnLabel from "./SnLabel";
import SnRadioBox from "./SnRadioBox";
import SnCheckBox from "./SnCheckBox";
import SnSwitch from "./SnSwitch";
import SnDateMonthBox from "./SnDateMonthBox";
import SnDateYearBox from "./SnDateYearBox";
import SnPeriodMonthBox from "./SnPeriodMonthBox";
import SnPeriodMonthBoxPop from "./SnPeriodMonthBoxPop";
import SnYearPeriodBox from "./SnYearPeriodBox";
import SnYearWeekPicker from "./SnYearWeekPicker";
import SnMultiCodeBox from "@shared/components/SnMultiCodeBox";
import SnMultiComboBox from "@shared/components/SnMultiComboBox";
import SnComboPeriodBox from "@shared/components/SnComboPeriodBox";
import SnCheckPeriodBox from "@shared/components/SnCheckPeriodBox";
import SnComboTextBox from "@shared/components/SnComboTextBox";
import SnItemDetailBox from "@shared/components/SnItemDetailBox";
import SnItemDetail from "@shared/components/SnItemDetail";
import SnItemInfoBox from "@shared/components/SnItemInfoBox";
import SnItemDetailBox2 from "@shared/components/SnItemDetailBox2";
import SnEmpty from "./SnEmpty";
import SnStyleCodeNTextBox from "@shared/components/SnStyleCodeNTextBox";
import SnMultiCodeNameBox from "@shared/components/SnMultiCodeNameBox";
import SnImageBox from "@shared/components/SnImageBox";
import SnProjectDetail from "./SnProjectDetail";
import SnMaskBox from "./SnMaskBox";
import { TpCtrl } from "@shared/type/CommonType";
import SnBarcodeBox from "./SnBarcodeBox";
import SnPeriodTextBox from "./SnPeriodTextBox";
import SnPeriodTimeBox from "./SnPeriodTimeBox";
import SnLinkedInfoBox from "./SnLinkedInfoBox";
import SnChipInput, { SnChipInputItem } from "./SnChipInput";
import SnAudioRecorder, { SnAudioValue } from "./SnAudioRecorder";
import SnTranscriptView from "./SnTranscriptView";
import SnEditor from "./SnEditor";
import SnHtmlTemplateEditor from "./SnHtmlTemplateEditor";
import { parseVarProps } from "@widgets/commonpopup/VarPropsPanel";
import { parseVarSet } from "@widgets/commonpopup/VarSetListPopup";
import SnVarPropsBox from "./SnVarPropsBox";
import SnVarSetBox from "./SnVarSetBox";
import SnFileUploadBox from "./SnFileUploadBox";
import { getMeetingAudio, setMeetingAudio } from "@shared/libs/meetingAudioStore";

interface Control {
  tp_ctrl: TpCtrl;
  no_line: string;
  qt_rowindex: number;
  qt_colindex: number;
  qt_rowspan: number;
  qt_colspan: number;
  nm_label?: string;
  cd_param: string;
  cd_value?: string;
  tp_format?: string;
  cd_displaycolumn?: string;
  cd_popup?: string;
  cd_clas?: string;
  dc_sql?: string;
}

interface Props {
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
  control: Control;
  index: number;
  formData: any;
  requireds: Record<string, boolean>;
  popupCons: Record<string, any>;
  popupRef?: any;
  setRef: (line: string, node: HTMLDivElement | null) => void;
  setInputRef: (el: any, index: number, isReadOnly: boolean) => void;
  handleMoveFocus: (index: number, target: string) => void;
  onTextValueChanged: (param: string, data: string) => void;
  onNumberValueChanged: (param: string, data: number) => void;
  onPeriodValueChanged: (
    param: string,
    start: string,
    end: string,
    source?: string
  ) => void;
  onCodeValueChanged: (
    code: string,
    data: string,
    name: string,
    data2: string,
    rowData?: any
  ) => void;
  setReadOnly: (param: string) => boolean;
  onButtonClick: (param: string) => void;
  filter: Record<string, any>;
  onEnter?: (param: string, value: string) => Promise<void> | void;
}

// 추가 필드 목록
const itemDetailMap: Record<string, string[]> = {
  cd_item: [
    "nm_item",
    "dc_item_spec",
    "dc_item_unit",
    "nm_model",
    "yn_use",
    "no_rev",
  ],
};

/**
 * 목적: formData에 문자열 또는 배열로 들어온 jsonb 값을 공통 배열 형태로 변환한다.
 *
 * 호출 예:
 * const rows = parseJsonArray(formData?.js_participant);
 *
 * 주요 기능:
 * DB 조회 결과가 JSON 문자열, 배열, 객체인 경우 모두 신규 메타 컨트롤에서 사용할 수 있게 맞춘다.
 *
 * @param value jsonb 컬럼 값 또는 JSON 문자열
 * @returns 파싱된 배열. 파싱 실패 또는 빈 값이면 빈 배열을 반환한다.
 */
const parseJsonArray = (value: any): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseJsonArray(item));
  }
  if (
    typeof value === "object" &&
    typeof value.value === "string" &&
    (value.type === "jsonb" || value.type === "json")
  ) {
    return parseJsonArray(value.value);
  }
  if (typeof value === "object") return [value];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parseJsonArray(parsed);
    } catch (error) {
      return [value];
    }
  }
  return [];
};

/**
 * 목적: jsonb 배열 값을 SnChipInput이 사용하는 Chip item 배열로 변환한다.
 *
 * 호출 예:
 * const chips = toChipItems(formData?.js_tag, "dc_tag");
 *
 * 주요 기능:
 * 문자열 배열과 객체 배열을 모두 지원하고, valueKey/cd_displaycolumn 기준으로 label 값을 만든다.
 *
 * @param value jsonb 컬럼 값 또는 JSON 문자열
 * @param valueKey Chip label로 사용할 객체 key
 * @returns SnChipInput item 배열
 */
const toChipItems = (value: any, valueKey?: string): SnChipInputItem[] =>
  parseJsonArray(value)
    .map((item: any) =>
      typeof item === "string"
        ? item
        : item?.[valueKey || "value"] ||
          item?.dc_tag ||
          item?.dc_participant ||
          item?.value ||
          item?.label ||
          item?.name ||
          ""
    )
    .filter(Boolean)
    .map((label: string) => ({ label, value: label }));

/**
 * 목적: SnChipInput의 Chip item 배열을 저장용 jsonb 배열 구조로 변환한다.
 *
 * 호출 예:
 * const jsonRows = toChipJson(chips, "dc_participant");
 *
 * 주요 기능:
 * sys_formctrl.cd_displaycolumn 또는 cd_param으로 지정된 key에 Chip 값을 담는다.
 *
 * @param items SnChipInput item 배열
 * @param valueKey 저장용 객체 key
 * @returns jsonb 저장 대상 배열
 */
const toChipJson = (items: SnChipInputItem[], valueKey?: string) =>
  items.map((item) => ({ [valueKey || "value"]: item.value || item.label }));

/**
 * Purpose: choose the jsonb object key used by chip-style controls.
 * Called from: SnChipInput renderer.
 */
const getChipValueKey = (param: string, displayColumn?: string) => {
  if (displayColumn) return displayColumn;
  if (param === "js_tag") return "dc_tag";
  if (param === "js_participant") return "dc_participant";
  return param;
};

/**
 * 목적: SnEditor가 사용할 본문 문자열을 jsonb 값에서 추출한다.
 *
 * 호출 예:
 * const body = extractTextFromJson(formData?.js_minutes, "txt_minutes");
 *
 * 주요 기능:
 * 회의록 에디터 저장 구조의 객체 또는 첫 번째 row에서 본문 key 값을 읽어온다.
 *
 * @param value jsonb 컬럼 값 또는 JSON 문자열
 * @param valueKey 본문 문자열을 담은 객체 key
 * @returns 에디터 본문 문자열
 */
const extractTextFromJson = (value: any, valueKey?: string) => {
  const rows = parseJsonArray(value);
  return rows?.[0]?.[valueKey || "text"] || "";
};

/**
 * 목적: JSON 문자열 스칼라로 감싸인 원문을 벗겨낸다.
 *
 * 주요 기능:
 * HTML을 jsonb 컬럼에 넣으면 JSON 스칼라("...\n...")로 저장돼, 읽을 때 따옴표로 감싸이고
 * 개행/따옴표가 \n, \" 로 이스케이프된 상태로 내려온다. 그대로 그리면 화면에 \n이 글자로 찍힌다.
 * HTML 원문은 절대 "로 시작하지 않으므로, 앞뒤가 "인 경우만 풀어낸다(잘못 벗길 위험 없음).
 * 저장 경로에 따라 두 번 감싸이는 경우가 있어 몇 번만 반복한다.
 *
 * @param text 컬럼에서 읽은 문자열
 * @returns 이스케이프가 풀린 원문
 */
const unwrapJsonString = (text: string): string => {
  let out = text;
  for (let i = 0; i < 3; i++) {
    const trimmed = out.trim();
    if (!trimmed.startsWith('"') || !trimmed.endsWith('"')) break;
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== "string") break;
      out = parsed;
    } catch {
      break;
    }
  }
  return out;
};

/**
 * 목적: 컬럼 값을 원문 문자열 하나로 만든다 (양식 HTML처럼 파싱하면 안 되는 값).
 *
 * 호출 예:
 * const html = toRawText(formData?.js_body);
 *
 * 주요 기능:
 * 드라이버/매퍼에 따라 raw 컬럼이 문자열이 아니라 {type:'jsonb', value:'...'} 로
 * 감싸여 오거나 배열로 내려온다. 그대로 넘기면 문자열 메서드에서 터지므로 풀어준다.
 * jsonb에 JSON 스칼라로 저장된 경우의 이스케이프도 여기서 벗긴다.
 *
 * @param value 컬럼 값
 * @returns 원문 문자열. 알 수 없는 형태면 빈 문자열
 */
const toRawText = (value: any): string => {
  if (value == null) return "";
  if (typeof value === "string") return unwrapJsonString(value);
  if (Array.isArray(value)) return value.map(toRawText).join("");
  if (typeof value === "object" && typeof (value as any).value === "string") {
    return toRawText((value as any).value);
  }
  return "";
};

/**
 * 목적: 변수 설정 컨트롤이 보여줄 목록을 고른다.
 *
 * 호출 예:
 * const rows = resolveVarSetValue(formData, control.cd_param);
 *
 * 주요 기능:
 * 이 컨트롤은 조회 전용이라 자기 값을 쓰지 않는다. 지정된 컬럼에 값이 없으면
 * 변수 속성(SnVarProps)의 기본 컬럼인 js_param에서 읽어 보여준다.
 *
 * @param formData 현재 행 데이터
 * @param param 컨트롤에 지정된 컬럼명
 * @returns 화면에 보여줄 변수 목록
 */
const resolveVarSetValue = (formData: any, param: string) => {
  const own = parseVarSet(formData?.[param]);
  return own.length > 0 ? own : parseVarSet(formData?.js_param);
};

const SnFindFormContols: React.FC<Props> = React.memo(
  ({
    isSearchPanel = false,
    isFormPanel = false,
    control,
    index,
    formData,
    requireds,
    popupCons,
    popupRef,
    setInputRef,
    handleMoveFocus,
    onTextValueChanged,
    onNumberValueChanged,
    onPeriodValueChanged,
    onCodeValueChanged,
    setReadOnly,
    onButtonClick,
    filter,
    onEnter,
  }) => {
    const isReadOnly = setReadOnly(control.cd_param) || formData == null;
    const tpCtrl = control.tp_ctrl.trim().replace("AfterLabel", "") as TpCtrl;

    const commonBoxProps = {
      inputRef: (el: any) => setInputRef(el, index, isReadOnly),
      onFocus: (target: string) => handleMoveFocus(index, target),
      isReadOnly,
      isEditable: formData != null,
    };

    let inputComponent: React.ReactNode = null;

    switch (tpCtrl) {
      case "SnTextBox":
      case "SnPasswordBox":
        /* SnPasswordBox = 입력이 별표로 가려지는 SnTextBox. 값 처리는 같다 (2026-09-17, AI 센터 연결 비밀번호 칸) */
        inputComponent = (
          <SnTextBox
            {...commonBoxProps}
            inputType={tpCtrl === "SnPasswordBox" ? "password" : undefined}
            isSearchPanel={isSearchPanel}
            onEnter={(data: string) => onEnter?.(control.cd_param, data)}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
            format={control.tp_format}
            isRequired={Object.hasOwn(requireds, control.cd_param)}
            popupRef={popupRef}
          />
        );
        break;

      case "SnNumberBox":
        inputComponent = (
          <SnNumberBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : 0
            }
            onValueChanged={(data: number) =>
              onNumberValueChanged(control.cd_param, data)
            }
            format={control.tp_format}
          />
        );
        break;

      case "SnMaskBox":
        inputComponent = (
          <SnMaskBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : 0
            }
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
            format={control.tp_format}
          />
        );
        break;

      case "SnPeriodMonthBox":
        const defaultPeriodMonthBoxValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");

        inputComponent = (
          <SnPeriodMonthBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultPeriodMonthBoxValue}
            onValueChanged={(start: string, end: string) =>
              onPeriodValueChanged(control.cd_param, start, end)
            }
          />
        );
        break;
      case "SnPeriodMonthBoxPop":
        const defaultPeriodMonthBoxPopValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");

        inputComponent = (
          <SnPeriodMonthBoxPop
            code={control.cd_popup ? control.cd_popup : ""}
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultPeriodMonthBoxPopValue}
            onValueChanged={(start: string, end: string, source?: string) =>
              onPeriodValueChanged(control.cd_param, start, end, source)
            }
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;
      case "SnCodeBox":
        inputComponent = (
          <SnCodeBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param]
                ? {
                    code: formData[control.cd_param],
                    name: formData[control.cd_displaycolumn || ""],
                  }
                : { code: "", name: "" }
            }
            code={control.cd_popup ? control.cd_popup : ""}
            cd_clas={control.cd_clas}
            onValueChanged={(code: string, name: string, rowData?: any) =>
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              )
            }
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;

      case "SnMultiCodeBox":
        inputComponent = (
          <SnMultiCodeBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            code={control.cd_popup ? control.cd_popup : ""}
            cd_clas={control.cd_clas}
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
            // 메인화면 조회조건(cd_biz 등)을 도움창으로 전달 — SnCodeBox와 동일.
            // 빠져 있으면 도움창이 사용자 기본 사업장으로 뜬다(ISS2026100136).
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;

      case "SnMultiComboBox":
        inputComponent = (
          <SnMultiComboBox
            defaultValue={
              formData?.[control.cd_param]
                ? String(formData[control.cd_param])
                    .split(",")
                    .map((c: string) => c.trim())
                    .filter((c: string) => c)
                : []
            }
            code={control.cd_clas}
            sql={(control as any).dc_sql}
            js_combo={(control as any).js_combo}
            isReadOnly={isReadOnly}
            isRequired={(control as any).yn_required === "Y"}
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
          />
        );
        break;

      case "SnDateBox":
        inputComponent = (
          <SnDateBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
          />
        );
        break;

      case "SnDateMonthBox":
        inputComponent = (
          <SnDateMonthBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
          />
        );
        break;

      case "SnDateYearBox":
        inputComponent = (
          <SnDateYearBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(data: string) =>
              onTextValueChanged(control.cd_param, data)
            }
          />
        );
        break;

      case "SnPeriodBox":
        const defaultPeriodBoxValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");

        inputComponent = (
          <SnPeriodBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultPeriodBoxValue}
            onValueChanged={(start: string, end: string) =>
              onPeriodValueChanged(control.cd_param, start, end)
            }
          />
        );
        break;

      case "SnYearWeekPicker":
        const defaultYearWeekValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");
        inputComponent = (
          <SnYearWeekPicker
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultYearWeekValue}
            isRequired={Object.hasOwn(requireds, control.cd_param)}
            onValueChanged={(yy: string, no_week: string) =>
              onPeriodValueChanged(control.cd_param, yy, no_week)
            }
          />
        );
        break;

      case "SnYearPeriodBox":
        const defaultDateYearButtonValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");
        inputComponent = (
          <SnYearPeriodBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultDateYearButtonValue}
            onValueChanged={(start: string, end: string) =>
              onPeriodValueChanged(control.cd_param, start, end)
            }
          />
        );
        break;

      case "SnCodeNTextBox":
        inputComponent = (
          <SnCodeNTextBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? {
                    code: formData[control.cd_param],
                    name: formData[control.cd_displaycolumn || ""],
                  }
                : { code: "", name: "" }
            }
            code={control.cd_popup ? control.cd_popup : ""}
            cd_clas={control.cd_clas}
            isReadOnly={isReadOnly}
            onValueChanged={(code: string, name: string, rowData?: any) =>
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              )
            }
            isEditable={formData != null}
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;

      case "SnComboBox":
        inputComponent = (
          <SnComboBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            isFormPanel={isFormPanel}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? formData[control.cd_param]
                : ""
            }
            code={control.cd_clas}
            sql={(control as any).dc_sql}
            js_combo={(control as any).js_combo}
            filter={
              typeof filter === "object" &&
              filter !== null &&
              Object.hasOwn(filter, control.cd_param) &&
              formData != null &&
              Object.hasOwn(formData, control.cd_param)
                ? filter?.[control?.cd_param]
                : ""
            }
            isReadOnly={isReadOnly}
            isRequired={(control as any).yn_required === "Y"}
            onValueChanged={(value: string) =>
              onTextValueChanged(control.cd_param, value)
            }
            isEditable={formData != null}
          />
        );
        break;

      case "SnStepUp":
        inputComponent = (
          <SnStepUp
            {...commonBoxProps}
            isReadOnly={isReadOnly}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? formData[control.cd_param]
                : ""
            }
            label={(control as any).nm_label}
            code={control.cd_clas}
            sql={(control as any).dc_sql}
            onValueChanged={(value: string) =>
              onTextValueChanged(control.cd_param, value)
            }
          />
        );
        break;

      case "SnRadioBox":
        inputComponent = (
          <SnRadioBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? formData[control.cd_param]
                : ""
            }
            code={control.cd_clas}
            sql={control.dc_sql}
            js_combo={(control as any).js_combo}
            isReadOnly={isReadOnly}
            onValueChanged={(value: string) =>
              onTextValueChanged(control.cd_param, value)
            }
          />
        );
        break;

      case "SnMemoBox": {
        const memoBox = (
          <SnMemoBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(value: string) =>
              onTextValueChanged(control.cd_param, value)
            }
          />
        );
        // 전사 원문(cd_value === "TRANSCRIPT", 2026-10-23): 보기(시각을 누르면 그 시점 재생) · 편집(메모 칸 그대로) 두 모드.
        // SnEditor 의 cd_value === "HTML" 과 같은 방식 — 다른 메모 칸은 cd_value 가 비어 그대로다.
        if (control.cd_value === "TRANSCRIPT") {
          inputComponent = (
            <SnTranscriptView
              text={String(formData?.[control.cd_param] ?? "")}
              rowKey={String(formData?.KeyField || "")}
              editor={memoBox}
              readOnly={isReadOnly}
            />
          );
          break;
        }
        inputComponent = memoBox;
        break;
      }

      case "SnCheckBox":
        inputComponent = (
          <SnCheckBox
            {...commonBoxProps}
            label={control.cd_displaycolumn || ""}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(value: string) =>
              onTextValueChanged(control.cd_param, value)
            }
          />
        );
        break;

      case "SnSwitch":
        const switchInitRaw = (
          formData?.[control.cd_param] ??
          (control as any).cd_value ??
          ""
        )
          .toString()
          .trim()
          .toUpperCase();
        const switchInitChecked = ["Y", "TRUE", "1", "ON"].includes(
          switchInitRaw
        );

        inputComponent = (
          // 폼 렌더 경로에서만 스위치 정렬/여백을 고정한다. (다른 메뉴 직접 SnSwitch 사용에는 영향 없음)
          <div className="w-full h-full flex items-center justify-start">
            <SnSwitch
              inputRef={(el: any) => setInputRef(el, index, isReadOnly)}
              disabled={isReadOnly}
              compact={true}
              checked={switchInitChecked}
              defaultChecked={switchInitChecked}
              labelCheck=""
              labelUncheck=""
              onChange={(_, checked) =>
                onTextValueChanged(control.cd_param, checked ? "Y" : "N")
              }
            />
          </div>
        );
        break;

      case "SnComboPeriodBox":
        const defaultComboPeriodBoxValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");

        inputComponent = (
          <SnComboPeriodBox
            {...commonBoxProps}
            defaultValue={defaultComboPeriodBoxValue}
            code={control.cd_clas}
            sql={control.dc_sql}
            js_combo={(control as any).js_combo}
            singleDateCodes={(control as any).tp_format}
            isSearchPanel={isSearchPanel}
            isFormPanel={isFormPanel}
            onValueChanged={(combo: string, start: string, end: string) => {
              onTextValueChanged(control.cd_param.split(",")[0].trim(), combo);

              const index = control.cd_param.indexOf(",");
              const periodParam =
                index !== -1
                  ? control.cd_param.substring(index + 1)
                  : control.cd_param;
              onPeriodValueChanged(periodParam, start, end);
            }}
          />
        );
        break;

      case "SnCheckPeriodBox":
        // cd_param: "체크값(Y/N), 시작일, 종료일" 3개 파라미터를 사용한다.
        const defaultCheckPeriodBoxValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");

        inputComponent = (
          <SnCheckPeriodBox
            {...commonBoxProps}
            defaultValue={defaultCheckPeriodBoxValue}
            isSearchPanel={isSearchPanel}
            isFormPanel={isFormPanel}
            onValueChanged={(check: string, start: string, end: string) => {
              onTextValueChanged(control.cd_param.split(",")[0].trim(), check);

              const index = control.cd_param.indexOf(",");
              const periodParam =
                index !== -1
                  ? control.cd_param.substring(index + 1)
                  : control.cd_param;
              onPeriodValueChanged(periodParam, start, end);
            }}
          />
        );
        break;

      case "SnComboTextBox":
        const defaultComboTextBoxValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");
        inputComponent = (
          <SnComboTextBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultComboTextBoxValue}
            code={control.cd_clas}
            sql={control.dc_sql}
            js_combo={(control as any).js_combo}
            onValueChanged={(combo: string, text: string) => {
              onTextValueChanged(control.cd_param.split(",")[0].trim(), combo);
              onTextValueChanged(control.cd_param.split(",")[1].trim(), text);
            }}
            format={control.tp_format}
            popupRef={popupRef}
          />
        );
        break;

      case "SnItemDetailBox":
        inputComponent = (
          <SnItemDetailBox
            {...commonBoxProps}
            isSearchPanel={true}
            defaultValue={{
              code: formData[control.cd_param],
              name: formData[control.cd_displaycolumn || ""],
            }}
            onValueChanged={(code: string, name: string, rowData?: any) => {
              // 1) 기존 공통 핸들러 호출(호환 유지)
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              );
              // 2) 추가 필드 자동 주입
              const extras = itemDetailMap[control.cd_param] ?? [];
              if (extras.length > 0) {
                extras.forEach((k) => {
                  const v = rowData?.[k] ?? "";
                  onTextValueChanged(k, v);
                });
              }
            }}
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;
      case "SnItemInfoBox":
        inputComponent = (
          <SnItemInfoBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param]
                ? {
                    code: formData[control.cd_param],
                    name: formData[control.cd_displaycolumn || ""],
                  }
                : { code: "", name: "" }
            }
            item_name={formData?.nm_item}
            dc_item_spec={formData?.dc_item_spec}
            dc_item_unit={formData?.dc_item_unit}
            code={"POPUP_ITEM"}
            cd_clas={control.cd_clas}
            onValueChanged={(code: string, name: string, rowData?: any) =>
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              )
            }
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;
      case "SnLinkedInfoBox": {
        const displayParams = control.cd_param
          .split(",")
          .map((param) => param.trim())
          .filter(Boolean);
        inputComponent = (
          <SnLinkedInfoBox
            label={control.nm_label ?? ""}
            values={displayParams.map((param) => formData?.[param] ?? "")}
          />
        );
        break;
      }
      case "SnItemDetailBox2":
        inputComponent = (
          <SnItemDetailBox2
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param]
                ? {
                    code: formData[control.cd_param],
                    name: formData[control.cd_displaycolumn || ""],
                  }
                : { code: "", name: "" }
            }
            code={"POPUP_ITEM"}
            cd_clas={control.cd_clas}
            onValueChanged={(code: string, name: string, rowData?: any) =>
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              )
            }
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;
      case "SnItemDetail":
        inputComponent = (
          <SnItemDetail
            {...commonBoxProps}
            isSearchPanel={true}
            code={control.cd_popup ? control.cd_popup : "POPUP_ITEM"}
            defaultValue={{
              code: formData[control.cd_param],
              name: formData[control.cd_displaycolumn || ""],
            }}
            onValueChanged={(code: string, name: string, rowData?: any) => {
              // 1) 기존 공통 핸들러 호출(호환 유지)
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              );
              // 2) 추가 필드 자동 주입
              const extras = itemDetailMap[control.cd_param] ?? [];
              if (extras.length > 0) {
                extras.forEach((k) => {
                  const v = rowData?.[k] ?? "";
                  onTextValueChanged(k, v);
                });
              }
            }}
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;
      case "SnStyleCodeNTextBox":
        inputComponent = (
          <SnStyleCodeNTextBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param]
                ? {
                    code: formData[control.cd_param],
                    name: formData[control.cd_displaycolumn || ""],
                  }
                : { code: "", name: "" }
            }
            code={control.cd_popup ? control.cd_popup : ""}
            cd_clas={control.cd_clas}
            isReadOnly={isReadOnly}
            onValueChanged={(code: string, name: string, rowData?: any) =>
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              )
            }
            isEditable={formData != null}
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;

      case "SnProjectDetail":
        inputComponent = (
          <SnProjectDetail
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData != null && Object.hasOwn(formData, control.cd_param)
                ? {
                    code: formData[control.cd_param],
                    name: formData[control.cd_displaycolumn || ""],
                  }
                : { code: "", name: "" }
            }
            code={control.cd_popup ? control.cd_popup : ""}
            cd_clas={control.cd_clas}
            isReadOnly={isReadOnly}
            onValueChanged={(code: string, name: string, rowData?: any) =>
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                rowData
              )
            }
            isEditable={formData != null}
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;

      case "SnButton":
        inputComponent = (
          <SnButton
            inputRef={(el: any) => setInputRef(el, index, true)}
            text={control.nm_label || control.cd_displaycolumn || ""}
            disabled={isReadOnly}
            onClick={() =>
              onButtonClick != null && onButtonClick(control.cd_param)
            }
          />
        );
        break;

      case "SnLabel":
        // 단독 텍스트 라벨(예: 숫자박스 뒤의 "분").
        // 앞쪽 고정폭(130px) 라벨 대신 셀 폭에 맞는 텍스트만 그린다.
        inputComponent = (
          <SnLabel text={control.nm_label || ""} type={"AfterLabel"} />
        );
        break;

      case "SnGroupLabel":
        inputComponent = (
          <SnGroupLabel
            inputRef={(el: any) => setInputRef(el, index, true)}
            onFocus={(target: string) => handleMoveFocus(index, target)}
            text={control.nm_label || ""}
          />
        );
        break;

      case "SnEmpty":
        inputComponent = (
          <SnEmpty
            inputRef={(el: any) => setInputRef(el, index, true)}
            onFocus={(target: string) => handleMoveFocus(index, target)}
          />
        );
        break;

      case "SnImageBox":
        inputComponent = (
          <SnImageBox
            {...commonBoxProps}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            code={
              formData?.[control.cd_param]
                ? formData[control.cd_displaycolumn || ""]
                : ""
            }
          />
        );
        break;

      case "SnMultiCodeNameBox":
        const codes = (formData?.[control.cd_param] || "")
          .split(",")
          .map((c: string) => c.trim())
          .filter((c: string) => c);

        const names = (formData?.[control.cd_displaycolumn || ""] || "")
          .split(",")
          .map((n: string) => n.trim());

        const defaultValue = codes.map((code: string, idx: number) => {
          const name = names[idx] || "";

          return {
            code,
            name,
            [control.cd_param as string]: code,
            [control.cd_displaycolumn as string]: name,
          };
        });

        inputComponent = (
          <SnMultiCodeNameBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultValue}
            code={control.cd_popup ? control.cd_popup : ""}
            cd_clas={control.cd_clas}
            onValueChanged={(code: string, name: string) => {
              onCodeValueChanged(
                control.cd_param,
                code,
                control.cd_displaycolumn || "",
                name,
                undefined
              );
            }}
            // 메인화면 조회조건(cd_biz 등)을 도움창으로 전달 — SnCodeBox와 동일.
            popupConditions={{
              ...formData,
              ...(popupCons[control.cd_param] || {}),
            }}
          />
        );
        break;

      case "SnBarcodeBox":
        inputComponent = (
          <SnBarcodeBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={""}
            onEnter={(data: string) => onEnter?.(control.cd_param, data)}
            format={control.tp_format}
            isRequired={Object.hasOwn(requireds, control.cd_param)}
            popupRef={popupRef}
          />
        );
        break;

      case "SnPeriodTextBox":
        inputComponent = (
          <SnPeriodTextBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={
              formData?.[control.cd_param] !== undefined
                ? formData[control.cd_param]
                : ""
            }
            onValueChanged={(start: string, end: string) =>
              onPeriodValueChanged(control.cd_param, start, end)
            }
            format={control.tp_format}
            isRequired={Object.hasOwn(requireds, control.cd_param)}
            popupRef={popupRef}
          />
        );
        break;

      case "SnPeriodTimeBox":
        // 시분초 기간(from ~ to). cd_param = "tm_start,tm_end" 형태로 두 키를 지정한다.
        const defaultPeriodTimeValue = control.cd_param
          .split(",")
          .map((param) => formData?.[param.trim()] ?? "")
          .join(",");
        inputComponent = (
          <SnPeriodTimeBox
            {...commonBoxProps}
            isSearchPanel={isSearchPanel}
            defaultValue={defaultPeriodTimeValue}
            onValueChanged={(start: string, end: string) =>
              onPeriodValueChanged(control.cd_param, start, end)
            }
            format={control.tp_format}
            isRequired={Object.hasOwn(requireds, control.cd_param)}
          />
        );
        break;

      case "SnChipInput":
        // 신규 메타 컨트롤: 참석자/태그처럼 여러 값을 Chip으로 입력하고 jsonb 배열 문자열로 FreeForm에 반영한다.
        // cd_value는 기본값 용도로만 사용하므로 placeholder 설정에 사용하지 않는다.
        const chipValueKey = getChipValueKey(
          control.cd_param,
          control.cd_displaycolumn
        );
        inputComponent = (
          <SnChipInput
            value={toChipItems(formData?.[control.cd_param], chipValueKey)}
            placeholder="Enter, Comma 입력"
            disabled={isReadOnly}
            onChange={(value) =>
              onTextValueChanged(
                control.cd_param,
                JSON.stringify(toChipJson(value, chipValueKey))
              )
            }
          />
        );
        break;

      case "SnAudioRecorder": {
        // 신규 메타 컨트롤: 녹음 또는 음성파일 업로드.
        // 실제 Blob 은 grid row 에 실을 수 없어 화면 보관소에 행(KeyField)마다 얹어둔다. 화면이 전사 · 저장 때 올리고
        // 그때 js_attach 의 AUDIO 항목(nm_file · dc_url)을 쓴다(2026-10-22). 예전에는 녹음하자마자 js_attach 를
        // dc_url 없는 값으로 통째로 바꿔, 올리기 전에 저장된 음성 연결이 지워졌다.
        // 저장된 행이면 js_attach의 AUDIO 항목(dc_url)을 녹음기에 넘겨 미리듣기가 되게 한다.
        const audioAttachList = parseJsonArray(formData?.[control.cd_param]) || [];
        const savedAudioAttach = audioAttachList.find(
          (item: any) => item?.tp_file === "AUDIO" && item?.dc_url
        );
        const audioRowKey = String(formData?.KeyField || "");
        inputComponent = (
          <SnAudioRecorder
            // 행이 바뀌면 이전 음성/재생 상태를 초기화하도록 행 식별자를 넘긴다.
            rowKey={audioRowKey}
            // 아직 올리지 않은 이 행의 음성(다른 행을 갔다 와도 다시 보인다)
            value={getMeetingAudio(audioRowKey)}
            savedAudio={
              savedAudioAttach
                ? { name: savedAudioAttach.nm_file, dcUrl: savedAudioAttach.dc_url }
                : null
            }
            disabled={isReadOnly}
            onChange={(value: SnAudioValue | null, rowKey?: string | number) => {
              const key = String(rowKey ?? audioRowKey);
              if (value) {
                setMeetingAudio(key, value);
                return;
              }
              // 지우기 — 아직 올리지 않은 음성이 있으면 그것만 지우고, 없으면 저장된 음성 연결을 뺀다
              if (getMeetingAudio(key)) {
                setMeetingAudio(key, null);
                return;
              }
              if (savedAudioAttach && key === audioRowKey) {
                onTextValueChanged(
                  control.cd_param,
                  JSON.stringify(
                    audioAttachList.filter((item: any) => item?.tp_file !== "AUDIO")
                  )
                );
              }
            }}
          />
        );
        break;
      }

      case "SnEditor":
        // 양식 HTML 템플릿(cd_value === "HTML"): 위지윅(CKEditor)은 전체문서/스타일/레이아웃
        // 테이블을 파싱하며 벗겨내므로 쓰지 않고, 원문을 무손실로 편집/미리보기하는 전용 에디터를 쓴다.
        // dc_editor 같은 raw HTML 컬럼을 JSON으로 감싸지 않고 그대로 바인딩한다.
        if (control.cd_value === "HTML") {
          inputComponent = (
            <div
              className="no-tailwind"
              style={{
                height: "100%",
                minHeight: 0,
                border: "1px solid rgb(186,191,199)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <SnHtmlTemplateEditor
                // raw HTML이라 문자열이어야 한다. jsonb로 감싸여 오는 경우가 있어 풀어준다.
                body={toRawText(formData?.[control.cd_param])}
                setBody={(value) => onTextValueChanged(control.cd_param, value)}
                // 툴바 "변수" 목록의 원천. 변수속성(SnVarProps)이 쓰는 컬럼을 그대로 읽는다.
                // 같은 행(formData)의 값이라 추가 조회가 없고, 저장 전 입력분도 바로 반영된다.
                // 컬럼명은 cd_displaycolumn으로 지정하고, 비어 있으면 js_param을 쓴다.
                variables={parseVarProps(
                  formData?.[control.cd_displaycolumn || "js_param"]
                )}
              />
            </div>
          );
          break;
        }
        // (기존 회의록) Rich Editor 본문을 js_minutes jsonb 객체 구조로 반영한다.
        // BE sfa_meetingh.js_minutes 타입이 Map이므로 배열이 아닌 객체로 저장한다.
        const editorValueKey = control.cd_displaycolumn || "txt_minutes";
        inputComponent = (
          <div
            className="no-tailwind"
            style={{
              height: "100%",
              minHeight: 0,
              border: "1px solid rgb(186,191,199)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <SnEditor
              uniqueId={`${control.cd_param}-${index}`}
              isReadOnly={isReadOnly}
              selectedData={formData?.no_meeting || "NEW"}
              body={extractTextFromJson(formData?.[control.cd_param], editorValueKey)}
              setBody={(value) =>
                onTextValueChanged(
                  control.cd_param,
                  JSON.stringify({ [editorValueKey]: value, text: value })
                )
              }
            />
          </div>
        );
        break;

      case "SnFileUploadBox":
        // 신규 메타 컨트롤: 기존 첨부파일 공통 컴포넌트를 회의록번호 기준으로 렌더링한다.
        // 헤더 단위 첨부는 no_line_docu 0을 사용하며, cd_value에 컨트롤 옵션을 싣지 않는다.
        inputComponent = (
          <SnFileUploadBox
            selectedDocu={formData?.no_meeting || ""}
            selectedLineDocu={0}
          />
        );
        break;

      case "SnVarProps":
        // 변수 속성(ID/Type/Default/Format) 목록 컨트롤.
        // cd_popup=POPUP_VARPROPS를 SnCodeBox로 붙이면 코드 1건만 담겨 목록이 안 되므로,
        // 목록 전체를 다루는 전용 컨트롤로 뺐다. 겉모습은 SnCodeBox와 같고,
        // cd_param에 jsonb 컬럼(js_param)을 지정하면 배열이 JSON 문자열로 그 컬럼에 실린다.
        inputComponent = (
          <SnVarPropsBox
            {...commonBoxProps}
            value={parseVarProps(formData?.[control.cd_param])}
            onChange={(next) =>
              onTextValueChanged(control.cd_param, JSON.stringify(next))
            }
            isReadOnly={isReadOnly}
          />
        );
        break;

      case "SnVarSet":
        // 변수 설정 — 등록된 변수를 보여주기만 하는 컨트롤(편집 없음).
        // 값의 원천은 변수 속성(SnVarProps)이 쓰는 컬럼이다. cd_param에 값이 없으면
        // 변수 속성의 기본 컬럼인 js_param을 본다. 그래서 컨트롤을 어느 컬럼에 걸어두든
        // 변수 속성에 등록한 내용이 그대로 뜬다.
        inputComponent = (
          <SnVarSetBox
            {...commonBoxProps}
            value={resolveVarSetValue(formData, control.cd_param)}
            isReadOnly={isReadOnly}
          />
        );
        break;
    }

    return <div className="h-full">{inputComponent}</div>;
  }
);

export default SnFindFormContols;
